// useUnsavedChanges — RC-1 Commercial QA · Unsaved Changes Protection.
// Presentation/workflow only. Zero engineering / RCE / storage impact.
//
// Detects whether the current editor's in-memory record differs from the last
// persisted baseline and intercepts every plausible way the operator can leave
// the page:
//   • Tab close / Refresh / Browser back to external URL → `beforeunload`
//   • Browser back / forward within RigSentinel        → React Router `useBlocker`
//   • App's Back button / any in-editor <Link>         → `guardedNavigate(to)`
//
// The dialog itself is rendered by the caller via <UnsavedChangesDialog>.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

/**
 * @param {object}   options
 * @param {any}      options.data            Current in-memory record (the source of truth for `dirty`).
 * @param {boolean}  [options.enabled=true]  When false the hook is a no-op (useful while the editor is loading).
 * @param {function} options.onSave          Existing Save handler. Must be sync or return a truthy result on success. Receives no args.
 */
export default function useUnsavedChanges({ data, enabled = true, onSave }) {
  const navigate = useNavigate();
  const baselineRef = useRef(_snap(data));
  const [dirty, setDirty]     = useState(false);
  const [dialog, setDialog]   = useState(null); // { pending: () => void }

  // Recompute dirty on every render. Stringify is cheap for our record sizes.
  useEffect(() => {
    if (!enabled) { setDirty(false); return; }
    setDirty(_snap(data) !== baselineRef.current);
  }, [data, enabled]);

  // Warn on tab close / refresh / navigate-to-external.
  useEffect(() => {
    if (!enabled || !dirty) return;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = ""; // Chrome/Edge require a truthy returnValue
      return "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [enabled, dirty]);

  // Intercept the browser Back button (popstate) within RigSentinel.
  // BrowserRouter is not a data router, so `useBlocker` isn't available; we
  // pin the current entry via history.pushState and, on popstate, if dirty
  // we prompt and only proceed on user confirmation.
  useEffect(() => {
    if (!enabled || !dirty) return;
    const sentinel = "rsc-unsaved-" + Date.now();
    window.history.pushState({ __rsc: sentinel }, "");
    const onPop = (e) => {
      // Immediately re-pin so the operator doesn't slip out while the
      // dialog is open.
      window.history.pushState({ __rsc: sentinel }, "");
      setDialog({
        pending: () => { setDialog(null); window.history.back(); window.history.back(); },
        cancel:  () => setDialog(null),
      });
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [enabled, dirty]);

  // Called by explicit in-app Back buttons / links that we own. `state` is
  // optional — Reference Library Return-Workflow uses it to hand a draft
  // back to the target route (see lib/reflibs/returnContext.js).
  const guardedNavigate = useCallback((to, state) => {
    const opts = state !== undefined ? { state } : undefined;
    if (!enabled || !dirty) { navigate(to, opts); return; }
    setDialog({
      pending: () => { setDialog(null); navigate(to, opts); },
      cancel:  () => setDialog(null),
    });
  }, [enabled, dirty, navigate]);

  // Called by callers after a successful Save — resets the baseline so the
  // record is no longer considered dirty.
  const markSaved = useCallback((next = data) => {
    baselineRef.current = _snap(next);
    setDirty(false);
  }, [data]);

  // "Save and Leave" handler — runs the caller's save then proceeds.
  const handleSaveAndLeave = useCallback(async () => {
    if (!dialog) return;
    try {
      const result = onSave ? await Promise.resolve(onSave()) : true;
      if (result === false) {
        // Save reported failure — leave the dialog open so the operator sees
        // the validation errors that the editor already renders.
        return;
      }
      // Baseline is updated by the editor's own post-save handler; we still
      // reset it defensively so a follow-up navigation doesn't re-block.
      baselineRef.current = _snap(data);
      const resume = dialog.pending;
      setDialog(null);
      resume?.();
    } catch {
      // Save threw — keep dialog open so operator can inspect the error.
    }
  }, [dialog, onSave, data]);

  // "Discard Changes" — proceed without saving.
  const handleDiscard = useCallback(() => {
    const resume = dialog?.pending;
    baselineRef.current = _snap(data); // avoid re-blocking on the same transition
    setDialog(null);
    resume?.();
  }, [dialog, data]);

  // "Cancel" — close the dialog and stay.
  const handleCancel = useCallback(() => {
    dialog?.cancel?.();
    setDialog(null);
  }, [dialog]);

  return useMemo(() => ({
    dirty,
    dialogOpen: Boolean(dialog),
    guardedNavigate,
    markSaved,
    handleSaveAndLeave,
    handleDiscard,
    handleCancel,
  }), [dirty, dialog, guardedNavigate, markSaved, handleSaveAndLeave, handleDiscard, handleCancel]);
}

function _snap(v) {
  try { return JSON.stringify(v ?? null); } catch { return ""; }
}
